import { Box } from '@mui/system';
import React, { useRef, useState } from 'react';
import InformationModal from '../../ifsp/InformationModal';
import MediumTypography from '../../../components/formlib/MediumTypography';
import DatePickerComponent from '../../../components/formlib/DatePickerComponent';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { updateIfspTransition } from '../../../redux/features/ifsp/ifspTransitionSlice';
import CommentTextbox from '../../../components/formlib/CommentTextbox';

import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { useIfspTransitionValidationCheckToSubmit } from './ifspTransitionValidationCheck';

type IfspTransitionPrepType = {
  isSaveClicked: boolean;
};

const IfspTransitionPrep = ({ isSaveClicked }: IfspTransitionPrepType) => {
  const ifspTransitionDetails = useAppSelector(
    (state) => state.ifspTransitionDetails.ifspTransitionData,
  );

  const validationsError = useIfspTransitionValidationCheckToSubmit(
    ifspTransitionDetails,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const dispatch = useAppDispatch();
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <Box component="main" className="mb-lg">
      <InformationModal
        open={openInfoModal}
        titleLabel="IFSP.Information"
        titleDefaultLabel="Information"
        buttonLabel="IFSP.InfoOkButton"
        buttonDefaultLabel="Ok"
        onClose={() => {
          setOpenInfoModal(false);
        }}
        infoLabelId="IFSP.TransitionPreparationInfo"
        infoDefaultLabel="Early Intervention services may occur for less than the specified period based on the child's age at the time of the eligibility evaluation. All EI services end one day prior to the child's third birthday. In preparation for transition, the IFSP Team will engage in a discussion about the upcoming changes including the specific programs, resources and supports that are available in the community. Individualized strategies for both the child and family are documented to ensure a smooth transition. If a child is potentially eligible for LEA/school or related services, additional steps are taken to complete a timely referral."
      />
      <Box
        component="section"
        className="p-lg borderRadius4"
        sx={{
          border:
            validationsError.transitionPreparationDate && isSaveClicked
              ? '1px solid red'
              : '1px solid #00C6B8',
          backgroundColor: '#ECF9F8',
        }}
      >
        <Box component="div" className="flex__justify__space-between">
          {openAccordian ? (
            <Box component="div" className="flex__justify__space-between">
              <MediumTypography
                labelid="IFSP.session8"
                defaultlabel="Transition Preparation"
                fontSize="18px"
                fontweight={600}
                textColor="#2A4241"
                className="pr-md"
              />
              <InfoIcon
                className="cursorPointer"
                onClick={() => {
                  setOpenInfoModal(true);
                }}
              />
            </Box>
          ) : (
            <MediumTypography
              labelid="IFSP.session8"
              defaultlabel="Transition Preparation"
              fontSize="18px"
              fontweight={600}
              textColor="#2A4241"
            />
          )}
          {!openAccordian ? (
            <DownArrow
              className="cursorPointer"
              style={{ color: '#008C82' }}
              onClick={() => {
                setOpenAccordian(!openAccordian);
              }}
            />
          ) : (
            <UpArrow
              className="cursorPointer"
              onClick={() => {
                setOpenAccordian(!openAccordian);
              }}
            />
          )}
        </Box>
        {openAccordian && (
          <Box component="div" className="mt-lg">
            <Box
              ref={parentRef}
              style={{
                pointerEvents: !ifspPermission.hasClientEditPermission
                  ? 'none'
                  : 'auto',
                opacity: !ifspPermission.hasClientEditPermission ? '0.5' : '1 ',
              }}
              onKeyDownCapture={(e) => {
                if (!ifspPermission.hasClientEditPermission) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onFocus={() => {
                if (!ifspPermission.hasClientEditPermission) {
                  if (parentRef.current) parentRef.current.focus();
                }
              }}
            >
              <Box className="width300px">
                <DatePickerComponent
                  key={
                    ifspTransitionDetails.transitionPreparation
                      .transitionPreparationDate
                  }
                  className="width200px bg__white"
                  labelid={'Date'}
                  defaultlabelid={'Date'}
                  value={
                    ifspTransitionDetails.transitionPreparation
                      .transitionPreparationDate
                      ? dayjs(
                          ifspTransitionDetails.transitionPreparation
                            .transitionPreparationDate,
                          'MM/DD/YYYY',
                        )
                      : null
                  }
                  handlechange={(value: Dayjs | null) => {
                    const formatedDate = value
                      ? value.format('MM/DD/YYYY')
                      : null;
                    dispatch(
                      updateIfspTransition({
                        ...ifspTransitionDetails,
                        transitionPreparation: {
                          ...ifspTransitionDetails.transitionPreparation,
                          transitionPreparationDate: formatedDate,
                        },
                      }),
                    );
                  }}
                />
                {isSaveClicked &&
                  validationsError.transitionPreparationDate && (
                    <Box className="loginError">
                      <MediumTypography
                        labelid={
                          validationsError.transitionPreparationDateErrorMessageId
                        }
                        defaultlabel={
                          validationsError.transitionPreparationDateErrorMessage
                        }
                        className="errorText-md"
                      />
                    </Box>
                  )}
              </Box>
              <Box className="mt-md">
                <MediumTypography
                  sxProps={{
                    marginLeft: '4px',
                    marginRight: '24px',
                    fontFamily: 'Lato-Regular',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  labelid={'IFSP.WhatChangesFewMonths'}
                  defaultlabel={
                    'What changes are coming up for our family in the next few months?'
                  }
                />
                <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                  <MediumTypography
                    sxProps={{
                      marginLeft: '4px',
                      marginRight: '24px',
                      fontFamily: 'Lato-Regular',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    labelid={'IFSP.WhatWillStayTheSame'}
                    defaultlabel={'What will stay the same?'}
                  />
                </Box>
                <Box sx={{ paddingTop: '8px' }}>
                  <CommentTextbox
                    className="translate"
                    name={'concern'}
                    placeholder={'Enter your comments'}
                    rows={5}
                    maxLength={5000}
                    Value={
                      ifspTransitionDetails.transitionPreparation
                        .stayTheSameComment
                        ? ifspTransitionDetails.transitionPreparation
                            .stayTheSameComment
                        : ''
                    }
                    handlechange={(value) => {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transitionPreparation: {
                            ...ifspTransitionDetails.transitionPreparation,
                            stayTheSameComment: value || null,
                          },
                        }),
                      );
                    }}
                    style={{
                      width: '840px',
                    }}
                  />
                </Box>
              </Box>

              <Box className="mt-md">
                <MediumTypography
                  sxProps={{
                    marginLeft: '4px',
                    marginRight: '24px',
                    fontFamily: 'Lato-Regular',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  labelid={'IFSP.WhatWillHelpUsPrepare'}
                  defaultlabel={'What will help us prepare?'}
                />
                <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                  <MediumTypography
                    sxProps={{
                      marginLeft: '4px',
                      marginRight: '24px',
                      fontFamily: 'Lato-Regular',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    labelid={'IFSP.WeSupportThisParticipation'}
                    defaultlabel={
                      'Are there new activities or routines that the child will need to participate in? How can we support this participation? (e.g. Visiting programs, meeting other parents)'
                    }
                  />
                </Box>
                <Box sx={{ paddingTop: '8px' }}>
                  <CommentTextbox
                    className="translate"
                    name={'concern'}
                    placeholder={'Enter your comments'}
                    rows={5}
                    maxLength={5000}
                    Value={
                      ifspTransitionDetails.transitionPreparation
                        .supportThisParticipationComment
                    }
                    handlechange={(value) => {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transitionPreparation: {
                            ...ifspTransitionDetails.transitionPreparation,
                            supportThisParticipationComment: value || null,
                          },
                        }),
                      );
                    }}
                    style={{
                      width: '840px',
                    }}
                  />
                </Box>
              </Box>

              <Box className="mt-md">
                <MediumTypography
                  sxProps={{
                    marginLeft: '4px',
                    marginRight: '24px',
                    fontFamily: 'Lato-Regular',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  labelid={'IFSP.WhatWillHelpUsPrepare'}
                  defaultlabel={
                    'Are there supports and resources that would be helpful?'
                  }
                />
                <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                  <MediumTypography
                    sxProps={{
                      marginLeft: '4px',
                      marginRight: '24px',
                      fontFamily: 'Lato-Regular',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    labelid={'IFSP.WeSupportThisParticipation'}
                    defaultlabel={
                      'What supports and resources are in place and will there be any gaps when our child leaves EI? (e.g. Transition to LEA/school information, community groups for support, information about transportation)'
                    }
                  />
                </Box>
                <Box sx={{ paddingTop: '8px' }}>
                  <CommentTextbox
                    className="translate"
                    name={'concern'}
                    placeholder={'Enter your comments'}
                    rows={5}
                    maxLength={5000}
                    Value={
                      ifspTransitionDetails.transitionPreparation
                        .gapsWhenChildLeavesEiComment
                    }
                    handlechange={(value) => {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transitionPreparation: {
                            ...ifspTransitionDetails.transitionPreparation,
                            gapsWhenChildLeavesEiComment: value || null,
                          },
                        }),
                      );
                    }}
                    style={{
                      width: '840px',
                    }}
                  />
                </Box>
              </Box>

              <Box className="mt-md">
                <MediumTypography
                  sxProps={{
                    marginLeft: '4px',
                    marginRight: '24px',
                    fontFamily: 'Lato-Regular',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  labelid={'IFSP.WhatWillHelpUsPrepare'}
                  defaultlabel={
                    'What community opportunities (if any) do we want to consider for our child?'
                  }
                />
                <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                  <MediumTypography
                    sxProps={{
                      marginLeft: '4px',
                      marginRight: '24px',
                      fontFamily: 'Lato-Regular',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    labelid={'IFSP.WeSupportThisParticipation'}
                    defaultlabel={
                      'List activities or services that may need to be explored. (e.g. Play groups, Preschool, on-going services)'
                    }
                  />
                </Box>
                <Box sx={{ paddingTop: '8px' }}>
                  <CommentTextbox
                    className="translate"
                    name={'concern'}
                    placeholder={'Enter your comments'}
                    rows={5}
                    maxLength={5000}
                    Value={
                      ifspTransitionDetails.transitionPreparation
                        .listActivitiesServiceComment
                    }
                    handlechange={(value) => {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transitionPreparation: {
                            ...ifspTransitionDetails.transitionPreparation,
                            listActivitiesServiceComment: value || null,
                          },
                        }),
                      );
                    }}
                    style={{
                      width: '840px',
                    }}
                  />
                </Box>
              </Box>

              <Box className="mt-md">
                <MediumTypography
                  sxProps={{
                    marginLeft: '4px',
                    marginRight: '24px',
                    fontFamily: 'Lato-Regular',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  labelid={'IFSP.WhatWillHelpUsPrepare'}
                  defaultlabel={
                    'What service changes (if any) do we need to support a smooth transition?'
                  }
                />
                <Box sx={{ alignItems: 'center', display: 'inline-flex' }}>
                  <MediumTypography
                    sxProps={{
                      marginLeft: '4px',
                      marginRight: '24px',
                      fontFamily: 'Lato-Regular',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                    labelid={'IFSP.WeSupportThisParticipation'}
                    defaultlabel={
                      'Are there changes in EI services that would help to prepare our child and family for the transition?'
                    }
                  />
                </Box>
                <Box sx={{ paddingTop: '8px' }}>
                  <CommentTextbox
                    className="translate"
                    name={'concern'}
                    placeholder={'Enter your comments'}
                    rows={5}
                    maxLength={5000}
                    Value={
                      ifspTransitionDetails.transitionPreparation
                        .changesInEiServiceComment
                    }
                    handlechange={(value) => {
                      dispatch(
                        updateIfspTransition({
                          ...ifspTransitionDetails,
                          transitionPreparation: {
                            ...ifspTransitionDetails.transitionPreparation,
                            changesInEiServiceComment: value || null,
                          },
                        }),
                      );
                    }}
                    style={{
                      width: '840px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IfspTransitionPrep;
